import type { ColumnDef } from '@tanstack/react-table';
import * as React from 'react';
import { useMemo } from 'react';
import { DataGrid } from 'src/shared/components/data-grid/data-grid';
import { useDataGrid } from 'src/shared/hooks/use-data-grid';

// Sanitize column ID to be valid for CSS custom property names (no spaces or special chars)
function sanitizeColumnId(header: string, index: number): string {
  const sanitized = header
    .replace(/[^a-zA-Z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return sanitized || `column_${index}`;
}

export function CsvTableV2({
  headers,
  rows,
  onDataChange,
  editable = true,
}: {
  headers: string[];
  rows: string[][];
  onDataChange: (data: string[][]) => void;
  editable?: boolean;
}) {
  // Create a mapping from sanitized IDs to original headers
  const columnMapping = useMemo(() => {
    return headers.map((header, index) => ({
      id: sanitizeColumnId(header, index),
      originalHeader: header,
    }));
  }, [headers]);

  // Derive data directly from props (controlled component pattern)
  const data = useMemo(
    () =>
      rows.map((row) =>
        row.reduce((acc, cell, index) => {
          const key = columnMapping[index]?.id ?? `column_${index}`;
          return { ...acc, [key]: cell };
        }, {})
      ),
    [rows, columnMapping]
  );

  // Helper to convert record array back to string[][] to update parent
  const handleDataChange = React.useCallback(
    (newData: Record<string, any>[]) => {
      if (columnMapping.length > 0) {
        onDataChange(newData.map((row) => columnMapping.map(({ id }) => row[id] ?? '')));
      } else {
        onDataChange(newData.map((row) => Object.values(row)));
      }
    },
    [columnMapping, onDataChange]
  );

  // Derive columns from headers or rows
  const columns = useMemo<ColumnDef<Record<string, any>>[]>(() => {
    if (columnMapping.length > 0) {
      return columnMapping.map(({ id, originalHeader }) => ({
        id: id,
        accessorKey: id,
        header: originalHeader, // Display the original header text
        meta: {
          cell: {
            variant: 'short-text',
          },
        },
        minSize: 180,
      }));
    } else {
      // Generate columns based on the first row's data if headers is empty
      if (rows.length > 0) {
        return rows[0].map((_, index) => ({
          id: `column_${index}`,
          accessorKey: `column_${index}`,
          header: `Column ${index + 1}`,
          meta: {
            cell: {
              variant: 'short-text',
            },
          },
          minSize: 180,
        }));
      }
      // Fallback for empty table
      return [
        {
          id: 'column_0',
          accessorKey: 'column_0',
          header: 'Column 1',
          meta: {
            cell: {
              variant: 'short-text',
            },
          },
          minSize: 180,
        },
      ];
    }
  }, [columnMapping, rows]);

  // Handle Row Addition
  const onRowAdd = React.useCallback(() => {
    let newRow: Record<string, any>;

    if (columnMapping.length > 0) {
      newRow = columnMapping.reduce((acc: Record<string, any>, { id }) => ({ ...acc, [id]: '' }), {});
    } else {
      if (rows.length > 0) {
        newRow = rows[0].reduce((acc: Record<string, any>, _, index) => ({ ...acc, [`column_${index}`]: '' }), {});
      } else {
        // Default for empty table
        newRow = { column_0: '' };
      }
    }

    const newData = [...data, newRow];
    handleDataChange(newData);

    return {
      rowIndex: data.length,
      columnId: columnMapping.length > 0 ? columnMapping[0].id : 'column_0',
    };
  }, [columnMapping, rows, data, handleDataChange]);

  // 5. Handle Row Deletion
  const onRowsDelete = React.useCallback(
    (rowsToDelete: Record<string, any>[], rowIndices: number[]) => {
      const newData = data.filter((_, index) => !rowIndices.includes(index));
      handleDataChange(newData);
    },
    [data, handleDataChange]
  );

  // 6. Initialize DataGrid hook
  const { table, ...dataGridProps } = useDataGrid({
    columns,
    data,
    onDataChange: editable ? handleDataChange : undefined,
    onRowAdd: editable ? onRowAdd : undefined,
    onRowsDelete: editable ? onRowsDelete : undefined,
    enableSearch: false,
    enableColumnPinning: false,
    enableSorting: false,
    enableHiding: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableColumnFilters: false,
    enableMultiRemove: false,
    enableMultiSort: false,
    enableColumnSelection: false,
    enableRowPinning: false,
  });

  return (
    <>
      <DataGrid {...dataGridProps} table={table} className="" />
    </>
  );
}
